import {useState, useEffect} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {updatePassword} from '../core/_requests'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {brandLogo} from '../../../tenant/applyBranding'
import {useIntl} from 'react-intl'
import {useLang} from '../../../../_metronic/i18n/Metronici18n'

const passwordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Minimum 8 characters')
    .max(50, 'Maximum 50 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .required('Password confirmation is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
})

const initialValues = {
  password: '',
  confirmPassword: '',
}

export function NewPassword() {
  const {formatMessage} = useIntl()
  const lang = useLang()
  const [loading, setLoading] = useState(false)
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const navigate = useNavigate()
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const tokenParam = urlParams.get('token')
    if (!tokenParam) {
      navigate('/auth/forgot-password')
    } else {
      setToken(tokenParam)
    }
  }, [navigate])

  const formik = useFormik({
    initialValues,
    validationSchema: passwordSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      try {
        if (!token) return
        const {data: updateResponse} = await updatePassword(
          token,
          values.password,
          values.confirmPassword
        )
        if (updateResponse.status_code === 'password_updated') {
          setHasErrors(false)
          setLoading(false)
          navigate('/auth/login?reset=1', {replace: true})
        } else {
          setHasErrors(true)
          setLoading(false)
          setSubmitting(false)
          setStatus(formatMessage({id: 'failed_to_update_password'}))
        }
      } catch (error) {
        setHasErrors(true)
        setLoading(false)
        setSubmitting(false)
        setStatus('Failed to update password')
      }
    },
  })

  if (!token) return null

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_new_password_form'
    >
      <div>
        <img
          alt=''
          width='170'
          className='mb-10 mb-10 mx-auto d-block'
          src={`${brandLogo(
            lang === 'ar',
            toAbsoluteUrl('/media/logos/logo_revamp' + (lang === 'ar' ? '-ar' : '') + '.svg')
          )}`}
        />
      </div>

      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>
          {formatMessage({id: 'AUTH.VERIFY.NEW_PASSWORD'})}
        </h1>
      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            {formatMessage({id: 'failed_to_update_password'})}
          </div>
        </div>
      )}

      {/* Password field */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-dark fs-6'>
          {formatMessage({id: 'AUTH.INPUT.PASSWORD'})}
        </label>
        <div className='position-relative'>
          <input
            type={passwordVisible ? 'text' : 'password'}
            autoComplete='new-password'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
            dir='ltr'
            style={{paddingInlineEnd: '3.5rem', backgroundImage: 'none', textAlign: 'left'}}
          />
          <button
            type='button'
            className='btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y me-2'
            onClick={() => setPasswordVisible((visible) => !visible)}
            aria-label={formatMessage({
              id: passwordVisible ? 'AUTH.PASSWORD.HIDE' : 'AUTH.PASSWORD.SHOW',
            })}
            aria-pressed={passwordVisible}
          >
            <i
              className={`fas ${passwordVisible ? 'fa-eye-slash' : 'fa-eye'}`}
              aria-hidden='true'
            />
          </button>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Password field */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-dark fs-6'>
          {formatMessage({id: 'AUTH.INPUT.CONFIRM_PASSWORD'})}
        </label>
        <div className='position-relative'>
          <input
            type={confirmPasswordVisible ? 'text' : 'password'}
            autoComplete='new-password'
            {...formik.getFieldProps('confirmPassword')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword,
              },
              {
                'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
              }
            )}
            dir='ltr'
            style={{paddingInlineEnd: '3.5rem', backgroundImage: 'none', textAlign: 'left'}}
          />
          <button
            type='button'
            className='btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y me-2'
            onClick={() => setConfirmPasswordVisible((visible) => !visible)}
            aria-label={formatMessage({
              id: confirmPasswordVisible ? 'AUTH.PASSWORD.HIDE' : 'AUTH.PASSWORD.SHOW',
            })}
            aria-pressed={confirmPasswordVisible}
          >
            <i
              className={`fas ${confirmPasswordVisible ? 'fa-eye-slash' : 'fa-eye'}`}
              aria-hidden='true'
            />
          </button>
        </div>
        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.confirmPassword}</span>
            </div>
          </div>
        )}
      </div>

      {/* Submit button */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_new_password_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && (
            <span className='indicator-label'>{formatMessage({id: 'AUTH.VERIFY.UPDATE'})}</span>
          )}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              {formatMessage({id: 'please_wait'})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>

      {/* Back to login link */}
      <div className='text-center'>
        <Link to='/auth/login' className='link-primary'>
          {formatMessage({id: 'AUTH.LOGIN.BUTTON'})}
        </Link>
      </div>
    </form>
  )
}
